import { Link } from "react-router-dom";
import { CONTACT } from "@/lib/products";
import { Instagram, Phone, Clock, MessageCircle, Mail } from "lucide-react";
import BrandLogo from "@/components/site/BrandLogo";
import { trackWhatsAppClick } from "@/lib/analytics";

export default function Footer() {
  return (
    <footer id="contact" className="relative border-t border-white/5 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <BrandLogo size={44} />
              <div>
                <div className="font-display text-3xl gold-gradient-text">Shine on Coin</div>
                <div className="text-xs uppercase tracking-widest-2 text-white/50">{CONTACT.tagline}</div>
              </div>
            </div>
            <p className="mt-6 text-white/60 leading-relaxed max-w-md">
              Bespoke engraved coins, lockets, mementos and bracelets, handcrafted
              in Hyderabad. Every keepsake tells a personal story.
            </p>

            <div id="story" className="mt-10 dashed-divider" />

            <div className="mt-8 text-xs uppercase tracking-widest-2 text-white/50">Our Studio Story</div>
            <p className="mt-2 text-white/70">
              What began as a single silver coin engraved for a family anniversary has grown into a
              boutique atelier delivering hundreds of personalized keepsakes across India.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Visit</div>
            <div className="mt-4 space-y-4 text-white/80">
              <ContactRow icon={<Clock size={16} />} label={CONTACT.hours} testid="footer-hours" />
              <ContactRow icon={<Phone size={16} />} label={CONTACT.phone} href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} testid="footer-phone" />
              <ContactRow
                icon={<Mail size={16} />}
                label={CONTACT.email}
                href={`mailto:${CONTACT.email}`}
                testid="footer-email"
              />
              <ContactRow
                icon={<MessageCircle size={16} />}
                label="Chat on WhatsApp"
                href={CONTACT.whatsapp}
                external
                testid="footer-whatsapp"
                onClick={() => trackWhatsAppClick({ placement: "footer" })}
              />
              <ContactRow
                icon={<Instagram size={16} />}
                label={CONTACT.instagramHandle}
                href={CONTACT.instagram}
                external
                testid="footer-instagram"
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Shop by Category</div>
            <ul className="mt-4 space-y-3 text-white/70 text-sm">
              <li><Link to="/catalog?type=coin" className="hover:text-[#d4af37] transition" data-testid="footer-cat-coin">Silver Coins</Link></li>
              <li><Link to="/catalog?type=locket" className="hover:text-[#d4af37] transition" data-testid="footer-cat-locket">Gold Lockets</Link></li>
              <li><Link to="/catalog?type=bracelet" className="hover:text-[#d4af37] transition" data-testid="footer-cat-bracelet">Bracelets</Link></li>
              <li><Link to="/catalog?type=plate" className="hover:text-[#d4af37] transition" data-testid="footer-cat-plate">Plate Mementos</Link></li>
              <li><Link to="/corporate" className="hover:text-[#d4af37] transition" data-testid="footer-cat-corporate">Corporate Gifting</Link></li>
              <li><Link to="/about" className="hover:text-[#d4af37] transition" data-testid="footer-cat-about">Our Story</Link></li>
              <li><Link to="/faq" className="hover:text-[#d4af37] transition" data-testid="footer-cat-faq">FAQ</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">The fine print</div>
            <ul className="mt-4 space-y-3 text-white/70 text-sm">
              <li>• Precious-metal personalised keepsakes in fine silver and gold.</li>
              <li>• {CONTACT.turnaround}</li>
              <li>• Every photo is retouched by hand before engraving.</li>
              <li>• Pan-India delivery. Bespoke enquiries welcome.</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs uppercase tracking-widest-2 text-white/40">
            © {new Date().getFullYear()} Shine on Coin · Made with care in Hyderabad
          </div>
          <div className="text-xs text-white/40">
            Bespoke engraving atelier
          </div>
        </div>
      </div>
    </footer>
  );
}

const ContactRow = ({ icon, label, href, external, testid, onClick }) => {
  const inner = (
    <div className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-full glass flex items-center justify-center text-[#d4af37]">{icon}</span>
      <span className="text-sm hover:text-[#d4af37] transition">{label}</span>
    </div>
  );
  if (!href) return <div data-testid={testid}>{inner}</div>;
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onClick={onClick}
      className="block"
      data-testid={testid}
    >
      {inner}
    </a>
  );
};
